import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtTRY } from "../lib/api";
import { itemTotal } from "./Shopping";
import { Plus, Pencil, Trash2, X, Check, Receipt, CreditCard, Scale, Camera, Upload, Loader2, Image as ImageIcon, Percent } from "lucide-react";
import Payments from "./Payments";
import OdemeDengesi from "./OdemeDengesi";
import RealizationModal from "../components/RealizationModal";
import { PayersSection } from "./Payments";

// SS2'deki sekmelere birebir uyumlu 5 grup:
// Genel Liste = tüm alışveriş kalemleri toplamı
const CAT_LABEL = {
  genel: "Genel Liste",
  yiyecek: "Yiyecek",
  icecek: "İçecekler",
  temizlik: "Temizlik",
  diger: "Diğer",
};

const emptyDraft = { category: "yiyecek", store: "", date: "", amount_try: "", description: "", payer_username: "", receipt_image: "", payers: [] };

export default function ActualCosts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "denge";
  const [tab, setTab] = useState(initialTab);
  const [actuals, setActuals] = useState([]);
  const [shopping, setShopping] = useState([]);
  const [cabins, setCabins] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const changeTab = (t) => {
    setTab(t);
    setSearchParams({ tab: t }, { replace: true });
  };

  const load = async () => {
    try {
      const [a, s, c] = await Promise.all([api.get("/actuals"), api.get("/shopping"), api.get("/cabins")]);
      setActuals(Array.isArray(a.data) ? a.data : []);
      setShopping(Array.isArray(s.data) ? s.data : []);
      setCabins(Array.isArray(c.data) ? c.data : []);
    } catch (e) { console.error("Muhasebe load:", e); }
  };
  useEffect(() => { load(); }, []);

  const allPersons = useMemo(() => {
    const list = [];
    for (const c of cabins) {
      const occ = c.occupants || [];
      const ppl = c.people || [];
      occ.forEach((un, i) => {
        if (!un) return;
        list.push({ username: un, display: (ppl[i]?.full_name) || un, cabin: c.name });
      });
    }
    return list;
  }, [cabins]);

  const estimatedByCat = useMemo(() => {
    // Alışveriş listesi kategorileri: yiyecek/icecek/temizlik/diger + genel toplam
    // Backend'in _effective_qty ile aynı hesap → itemTotal(item) note'tan sayı çeker
    const map = { genel: 0, yiyecek: 0, icecek: 0, temizlik: 0, diger: 0 };
    for (const i of shopping) {
      const total = itemTotal(i);
      map.genel += total;
      if (map[i.category] != null) map[i.category] += total;
    }
    return map;
  }, [shopping]);

  const actualByCat = useMemo(() => {
    const map = { genel: 0, yiyecek: 0, icecek: 0, temizlik: 0, diger: 0 };
    for (const a of actuals) {
      const amt = a.amount_try || 0;
      map.genel += amt;
      if (map[a.category] != null) map[a.category] += amt;
    }
    return map;
  }, [actuals]);

  const totalEstimated = estimatedByCat.genel;
  const totalActual = actualByCat.genel;

  const [lightboxSrc, setLightboxSrc] = useState("");
  const [confirmPayerModal, setConfirmPayerModal] = useState(false);
  const [showRealization, setShowRealization] = useState(false);

  const save = async (skipPayerCheck = false) => {
    const totalTry = parseFloat(String(draft.amount_try).replace(",", ".")) || 0;
    if (!draft.date || !totalTry) return;

    // PayersSection'dan gelen payers[] varsa çoklu-ödeyen olarak N fiş oluştur (herbiri kendi payer_username + tutar)
    const payersRaw = (Array.isArray(draft.payers) ? draft.payers : []).filter((p) => p && p.person_idx);
    if (payersRaw.length >= 1) {
      // Tek payer da splits[] üzerinden gelir → tek doc yaz
      // Birden fazla payer → N doc (aynı receipt_image + store + date + category, sadece amount+payer değişir)
      if (editingId) await api.delete(`/actuals/${editingId}`);
      for (const p of payersRaw) {
        // person_idx = username|0 (allPersons adapter'ında username=cabin_id, idx=0 verildi)
        const [username] = String(p.person_idx).split("|");
        const amt = parseFloat(String(p.amount).replace(",", ".")) || 0;
        if (amt <= 0) continue;
        const body = {
          category: draft.category,
          store: draft.store || "",
          date: draft.date,
          amount_try: amt,
          description: draft.description || "",
          payer_username: username || "",
          receipt_image: draft.receipt_image || "",
        };
        await api.post(`/actuals`, body);
      }
      setShowNew(false); setEditingId(null); setDraft(emptyDraft);
      setConfirmPayerModal(false);
      await load();
      return;
    }

    // Legacy fallback: payers boşsa eski tek payer_username akışı
    const body = {
      category: draft.category,
      store: draft.store || "",
      date: draft.date,
      amount_try: totalTry,
      description: draft.description || "",
      payer_username: draft.payer_username || "",
      receipt_image: draft.receipt_image || "",
    };
    if (!body.payer_username && !skipPayerCheck) {
      setConfirmPayerModal(true);
      return;
    }
    if (editingId) await api.put(`/actuals/${editingId}`, body);
    else await api.post(`/actuals`, body);
    setShowNew(false); setEditingId(null); setDraft(emptyDraft);
    setConfirmPayerModal(false);
    await load();
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    // Tek payer'ı payers[]'a doldur (person_idx = username|0)
    const initPayers = a.payer_username ? [{ person_idx: `${a.payer_username}|0`, amount: a.amount_try }] : [];
    setDraft({
      category: a.category,
      store: a.store || "",
      date: a.date,
      amount_try: a.amount_try,
      description: a.description || "",
      payer_username: a.payer_username || "",
      receipt_image: a.receipt_image || "",
      payers: initPayers,
    });
    setShowNew(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    await api.delete(`/actuals/${id}`);
    await load();
  };

  const personDisplay = (un) => allPersons.find((p) => p.username === un)?.display || un;

  const openMuhasebePdf = async (mode) => {
    try {
      const res = await api.get(`/muhasebe/export.pdf?mode=${mode}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank", "noopener");
      // Cleanup
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      console.error("Muhasebe PDF export failed:", e);
      alert("PDF alınırken bir hata oluştu. Lütfen tekrar deneyin.");
    }
  };

  return (
    <div className="space-y-5">
      {/* tabs — sağda Özet PDF, Genel PDF, Gerçekleşme %, +Yeni butonları */}
      <div className="flex gap-1.5 flex-wrap items-center" data-testid="muhasebe-tabs">
        <button
          onClick={() => changeTab("denge")}
          className={`tk-btn-ghost flex items-center gap-1.5 !py-1.5 !px-2.5 text-xs sm:text-sm sm:!py-2 sm:!px-3 ${tab === "denge" ? "!bg-[#dbe9ff] !text-[#4992ff]" : ""}`}
          data-testid="tab-denge"
        >
          <Scale className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="sm:hidden">Denge</span><span className="hidden sm:inline">Ödeme Dengesi</span>
        </button>
        <button
          onClick={() => changeTab("odemeler")}
          className={`tk-btn-ghost flex items-center gap-1.5 !py-1.5 !px-2.5 text-xs sm:text-sm sm:!py-2 sm:!px-3 ${tab === "odemeler" ? "!bg-[#dbe9ff] !text-[#4992ff]" : ""}`}
          data-testid="tab-odemeler"
        >
          <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="sm:hidden">Tekne</span><span className="hidden sm:inline">Tekne Ödemeleri</span>
        </button>
        <button
          onClick={() => changeTab("alisveris")}
          className={`tk-btn-ghost flex items-center gap-1.5 !py-1.5 !px-2.5 text-xs sm:text-sm sm:!py-2 sm:!px-3 ${tab === "alisveris" ? "!bg-[#dbe9ff] !text-[#4992ff]" : ""}`}
          data-testid="tab-alisveris"
        >
          <Receipt className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="sm:hidden">Alışveriş</span><span className="hidden sm:inline">Alışveriş Ödemeleri</span>
        </button>

        {/* Sağ blok: Özet PDF | Genel PDF | Gerçekleşme % | +Yeni (Ödeme Dengesi'nde son ikisi gizli) */}
        <div className="ml-auto flex items-center gap-1.5" data-testid="muhasebe-tabbar-actions">
          <button data-testid="muhasebe-pdf-ozet-btn" onClick={() => openMuhasebePdf("ozet")} className="tk-btn-ghost !py-1.5 !px-2 text-xs flex items-center gap-1" title="Muhasebe Özet PDF">
            <Receipt className="w-3.5 h-3.5" /> Özet
          </button>
          <button data-testid="muhasebe-pdf-genel-btn" onClick={() => openMuhasebePdf("genel")} className="tk-btn-ghost !py-1.5 !px-2 text-xs flex items-center gap-1" title="Muhasebe Genel PDF">
            <Receipt className="w-3.5 h-3.5" /> Genel
          </button>
          {tab !== "denge" && (
            <>
              <button
                data-testid="realization-open-btn"
                onClick={() => {
                  if (tab === "odemeler") {
                    window.dispatchEvent(new CustomEvent("muhasebe:open-realization", { detail: { scope: "diger" } }));
                  } else {
                    setShowRealization(true);
                  }
                }}
                className="tk-btn-ghost flex items-center gap-1.5"
                title={tab === "odemeler" ? "Tekne Diğer Maliyet tahminlerinin gerçekleşme yüzdesi" : "Alışveriş tahminlerinin gerçekleşme yüzdesi"}
              >
                <Percent className="w-4 h-4" /> Gerçekleşme %
              </button>
              <button
                data-testid="add-btn-tabbar"
                onClick={() => {
                  if (tab === "odemeler") {
                    window.dispatchEvent(new CustomEvent("muhasebe:new-payment"));
                  } else {
                    setShowNew(true); setEditingId(null); setDraft({ ...emptyDraft, date: new Date().toISOString().slice(0, 10) });
                  }
                }}
                className="tk-btn flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> Yeni
              </button>
            </>
          )}
        </div>
      </div>

      {tab === "denge" && <OdemeDengesi />}
      {tab === "odemeler" && <Payments />}
      {tab === "alisveris" && (<>

      {/* summary grid — SS2'deki 5 sekmeye göre: Genel Liste / Yiyecek / İçecekler / Temizlik / Diğer */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["genel", "yiyecek", "icecek", "temizlik", "diger"].map((cat) => {
          const est = estimatedByCat[cat] || 0;
          const act = actualByCat[cat] || 0;
          const diff = act - est;
          return (
            <div key={cat} className="tk-card p-4" data-testid={`cost-card-${cat}`}>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>{CAT_LABEL[cat]}</div>
              <div className="mt-1.5 text-sm">
                <div className="flex justify-between"><span style={{ color: "hsl(var(--text-2))" }}>Tahmini</span><b>{fmtTRY(est)}</b></div>
                <div className="flex justify-between"><span style={{ color: "hsl(var(--text-2))" }}>Gerçek</span><b style={{ color: "hsl(var(--primary))" }}>{fmtTRY(act)}</b></div>
                <div className="flex justify-between mt-1 pt-1 tk-divider"><span style={{ color: "hsl(var(--text-2))" }}>Fark</span>
                  <b style={{ color: diff > 0 ? "hsl(var(--error))" : "hsl(var(--success))" }}>{diff >= 0 ? "+" : ""}{fmtTRY(diff)}</b>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="tk-card p-5" data-testid="cost-grand-card">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Tahmini Toplam</div>
            <div className="mt-1 text-xl font-display font-semibold">{fmtTRY(totalEstimated)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Gerçek Toplam</div>
            <div className="mt-1 text-xl font-display font-semibold" style={{ color: "hsl(var(--primary))" }}>{fmtTRY(totalActual)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Fark</div>
            <div className="mt-1 text-xl font-display font-semibold" style={{ color: (totalActual - totalEstimated) > 0 ? "hsl(var(--error))" : "hsl(var(--success))" }}>
              {(totalActual - totalEstimated) >= 0 ? "+" : ""}{fmtTRY(totalActual - totalEstimated)}
            </div>
          </div>
        </div>
      </div>

      {showNew && (
        <div className="tk-card p-4" data-testid="new-actual-card">
          <ActualForm draft={draft} setDraft={setDraft} allPersons={allPersons} onSave={save} onCancel={() => { setShowNew(false); setDraft(emptyDraft); }} />
        </div>
      )}

      <div className="space-y-2">
        {actuals.length === 0 && !showNew && (
          <div className="tk-card p-6 text-center text-sm" style={{ color: "hsl(var(--text-2))" }}>
            Henüz gerçek maliyet girilmedi — tahminler kullanılıyor.
          </div>
        )}
        {actuals.map((a) => {
          if (editingId === a.id) {
            return (
              <div key={a.id} className="tk-card p-4" data-testid={`edit-actual-${a.id}`}>
                <ActualForm draft={draft} setDraft={setDraft} allPersons={allPersons} onSave={save} onCancel={() => { setEditingId(null); setDraft(emptyDraft); }} />
              </div>
            );
          }
          const fmtDate = new Date(a.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "2-digit" });
          return (
            <div key={a.id} data-testid={`actual-row-${a.id}`} className="tk-card p-4 flex items-start gap-3">
              {a.receipt_image && (
                <button
                  type="button"
                  data-testid={`actual-receipt-thumb-${a.id}`}
                  onClick={() => setLightboxSrc(a.receipt_image)}
                  className="flex-shrink-0 rounded-lg overflow-hidden border cursor-pointer"
                  style={{ borderColor: "hsl(var(--border))", width: 56, height: 56, padding: 0 }}
                  title="Fişi büyüt"
                >
                  <img src={a.receipt_image} alt="Fiş" className="w-full h-full object-cover" />
                </button>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="tk-chip">{CAT_LABEL[a.category] || a.category}</span>
                  {a.store && <span className="tk-chip">{a.store}</span>}
                  <span className="tk-chip">{fmtDate}</span>
                  {a.payer_username && <span className="tk-chip" style={{ backgroundColor: "hsl(var(--surface-2))" }}>Ödeyen: {personDisplay(a.payer_username)}</span>}
                </div>
                <div className="mt-1.5 text-lg font-display font-semibold" style={{ color: "hsl(var(--primary))" }}>{fmtTRY(a.amount_try)}</div>
                {a.description && <div className="text-xs mt-0.5" style={{ color: "hsl(var(--text-2))" }}>{a.description}</div>}
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`edit-actual-btn-${a.id}`} onClick={() => startEdit(a)} className="tk-btn-ghost !py-2 !px-2.5"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-actual-btn-${a.id}`} onClick={() => remove(a.id)} className="tk-btn-danger !py-2 !px-2.5"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
      </>)}

      {/* Fiş lightbox modalı */}
      {lightboxSrc && (
        <div
          data-testid="receipt-lightbox"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
          onClick={() => setLightboxSrc("")}
        >
          <button
            data-testid="receipt-lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightboxSrc(""); }}
            className="absolute top-4 right-4 tk-btn-ghost !p-2"
            style={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white" }}
            title="Kapat"
          ><X className="w-5 h-5" /></button>
          <img
            src={lightboxSrc}
            alt="Fiş büyük"
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full rounded-lg shadow-2xl"
          />
        </div>
      )}

      {/* Payer boş onay modalı */}
      {confirmPayerModal && (
        <div
          data-testid="confirm-payer-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="tk-card p-6 max-w-md w-full">
            <div className="text-lg font-display font-semibold mb-2">Ödeyen kişi seçmediniz</div>
            <div className="text-sm mb-4" style={{ color: "hsl(var(--text-2))" }}>
              Ödeyen kullanıcı seçilmediğinde netleştirmeye dahil olmaz. Yine de kaydetmek istiyor musunuz?
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                data-testid="confirm-payer-cancel"
                onClick={() => setConfirmPayerModal(false)}
                className="tk-btn-ghost flex items-center gap-1.5"
              ><X className="w-4 h-4" /> Vazgeç</button>
              <button
                data-testid="confirm-payer-save"
                onClick={() => save(true)}
                className="tk-btn flex items-center gap-1.5"
              ><Check className="w-4 h-4" /> Evet, Kaydet</button>
            </div>
          </div>
        </div>
      )}
      {/* Gerçekleşme % Modalı — Alışveriş scope */}
      <RealizationModal
        open={showRealization}
        onClose={() => setShowRealization(false)}
        onSaved={() => { /* dashboard read on next mount */ }}
        scope="shop"
      />
    </div>
  );
}

function ActualForm({ draft, setDraft, allPersons, onSave, onCancel }) {
  const upd = (k, v) => setDraft({ ...draft, [k]: v });
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [ocrLoading, setOcrLoading] = useState(false);

  const compressAndReadDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new window.Image();
        img.onload = () => {
          // Max width 1600px — küçük tut ki base64 payload makul kalsın (~200-400KB)
          const maxW = 1600;
          let w = img.width, h = img.height;
          if (w > maxW) { h = Math.round(h * (maxW / w)); w = maxW; }
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
          resolve(dataUrl);
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await compressAndReadDataURL(file);
      setDraft({ ...draft, receipt_image: dataUrl });
      setOcrLoading(true);
      try {
        const res = await api.post("/actuals/ocr-receipt", { image_base64: dataUrl });
        const d = res.data || {};
        setDraft((prev) => ({
          ...prev,
          receipt_image: dataUrl,
          store: d.store || prev.store,
          amount_try: d.amount_try ? String(d.amount_try) : prev.amount_try,
          description: d.description || prev.description,
        }));
      } catch (e) {
        console.error("OCR failed:", e);
      } finally {
        setOcrLoading(false);
      }
    } catch (e) {
      console.error("File read failed:", e);
    }
  };

  return (
    <div className="space-y-4">
      {/* Başlık — Fiş / Fatura & Ödeme Kayıtları (siyah H1) */}
      <h2 className="text-2xl font-display font-semibold" style={{ color: "hsl(var(--text-1))" }}>
        Fiş / Fatura &amp; Ödeme Kayıtları
      </h2>

      {/* Üst: Fiş fotoğrafı SOL (2 satır yüksek) + alanlar SAĞ (2 satır) */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-start">
        {/* SOL: fotoğraf alanı */}
        <div className="md:col-span-2 flex flex-col items-center gap-2">
          {draft.receipt_image ? (
            <div className="relative">
              <img
                src={draft.receipt_image}
                alt="Fiş önizleme"
                className="w-full h-48 object-cover rounded-lg border"
                style={{ borderColor: "hsl(var(--border))" }}
                data-testid="receipt-preview"
              />
              <button
                type="button"
                data-testid="receipt-remove-btn"
                onClick={() => upd("receipt_image", "")}
                className="absolute -top-2 -right-2 tk-btn-danger !p-1 rounded-full"
                title="Fotoğrafı kaldır"
              ><X className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <div
              className="w-full h-48 rounded-lg border-2 border-dashed flex items-center justify-center"
              style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--text-2))" }}
            >
              <ImageIcon className="w-8 h-8" />
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-2 w-full">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="receipt-file-input" />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="receipt-camera-input" />
            <button type="button" data-testid="receipt-upload-btn" onClick={() => fileInputRef.current?.click()} className="tk-btn-ghost flex items-center gap-1.5 text-xs" disabled={ocrLoading}>
              {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Yükle
            </button>
            <button type="button" data-testid="receipt-camera-btn" onClick={() => cameraInputRef.current?.click()} className="tk-btn-ghost flex items-center gap-1.5 text-xs" disabled={ocrLoading}>
              <Camera className="w-4 h-4" /> Kamera
            </button>
          </div>
          {ocrLoading && (
            <div className="text-xs flex items-center gap-1.5" style={{ color: "hsl(var(--primary))" }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Fiş okunuyor…
            </div>
          )}
        </div>

        {/* SAĞ: 2 satır x 3 sütun alanlar */}
        <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="tk-label">Kategori</label>
            <select data-testid="actual-cat-select" className="tk-input" value={draft.category} onChange={(e) => upd("category", e.target.value)}>
              <option value="yiyecek">Yiyecek</option>
              <option value="icecek">İçecekler</option>
              <option value="temizlik">Temizlik</option>
              <option value="diger">Diğer</option>
            </select>
          </div>
          <div>
            <label className="tk-label">Mağaza / Yer</label>
            <input data-testid="actual-store-input" className="tk-input" value={draft.store} onChange={(e) => upd("store", e.target.value)} placeholder="Migros, Kasap, Balıkçı..." />
          </div>
          <div>
            <label className="tk-label">Tarih</label>
            <input data-testid="actual-date-input" type="date" className="tk-input" value={draft.date} onChange={(e) => upd("date", e.target.value)} />
          </div>
          <div>
            <label className="tk-label">Tutar (₺) <span style={{ color: "hsl(var(--error))" }}>*</span></label>
            <input data-testid="actual-amount-input" className="tk-input" inputMode="decimal" value={draft.amount_try} onChange={(e) => upd("amount_try", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="tk-label">Açıklama <span style={{ color: "hsl(var(--text-2))" }}>(fiş/fatura no)</span></label>
            <input data-testid="actual-desc-input" className="tk-input" value={draft.description} onChange={(e) => upd("description", e.target.value)} />
          </div>
        </div>
      </div>

      {/* Ödeme Yapan(lar) — Tekne Ödemeleri'ndeki bloğun aynısı */}
      <PayersSection
        draft={{
          amount: draft.amount_try,
          currency: "TRY",
          splits: Array.isArray(draft.payers) ? draft.payers : [],
        }}
        setDraft={(v) => {
          const val = typeof v === "function" ? v({ splits: draft.payers || [] }) : v;
          setDraft({ ...draft, payers: Array.isArray(val.splits) ? val.splits : [] });
        }}
        allPersons={allPersons.map((p) => ({ cabin_id: p.username, idx: 0, full_name: p.display, cabin_name: p.cabin }))}
      />

      <div className="flex items-center justify-end gap-2">
        <button data-testid="actual-cancel-btn" onClick={onCancel} className="tk-btn-ghost flex items-center gap-1.5"><X className="w-4 h-4" /> Vazgeç</button>
        <button data-testid="actual-save-btn" onClick={() => onSave(false)} className="tk-btn flex items-center gap-1.5"><Check className="w-4 h-4" /> Kaydet</button>
      </div>
    </div>
  );
}
